import { Helmet } from "react-helmet-async";
import Layout from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";

const Disclaimer = () => {
  return (
    <Layout>
      <Helmet>
        <title>Disclaimer — Datalevo Tools | Walking Weight Loss Calculator</title>
        <meta
          name="description"
          content="Read the disclaimer for Datalevo's Walking Weight Loss Calculator. Understand the limitations and intended use of our health estimation tools."
        />
        <link rel="canonical" href="https://walkingweightlosscalculator.datalevo.com/disclaimer" />
      </Helmet>

      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Disclaimer
          </h1>
          <p className="text-muted-foreground mb-8">
            Last updated: December 4, 2025
          </p>

          {/* Important Notice */}
          <Card className="p-6 bg-destructive/10 border-destructive/20 mb-8">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-foreground mb-2">Important Medical Notice</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The Walking Weight Loss Calculator is an informational tool only. It is NOT intended to provide medical advice, diagnosis, or treatment. Always consult a qualified healthcare professional before making changes to your exercise or diet regimen.
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 md:p-8">
            <div className="prose prose-sm md:prose-base max-w-none text-foreground">
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">General Disclaimer</h2>
                <p className="text-muted-foreground leading-relaxed">
                  The information provided by the Walking Weight Loss Calculator ("the Calculator") on walkingweightlosscalculator.datalevo.com is for general informational and educational purposes only. All information is provided in good faith; however, we make no representation or warranty of any kind, express or implied, regarding the accuracy, adequacy, validity, reliability, availability, or completeness of any information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Not Medical Advice</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The Calculator is not a substitute for professional medical advice, diagnosis, or treatment. The calculations provided are estimates based on:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Population-based averages and validated scientific formulas</li>
                  <li>User-provided input data which may contain errors</li>
                  <li>General assumptions that may not apply to your individual situation</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Never disregard professional medical advice or delay seeking it because of something you calculated using our tool. If you think you may have a medical emergency, call your doctor or emergency services immediately.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Accuracy Limitations</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  While we strive for accuracy, the Calculator has inherent limitations:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong className="text-foreground">Individual Variation:</strong> Metabolism varies 10-20% between individuals of similar characteristics</li>
                  <li><strong className="text-foreground">Adaptive Thermogenesis:</strong> The body adapts to sustained calorie deficits in ways our formulas cannot predict</li>
                  <li><strong className="text-foreground">Medical Conditions:</strong> Thyroid disorders, medications, and other conditions affect metabolic rates</li>
                  <li><strong className="text-foreground">Input Accuracy:</strong> Results are only as accurate as the data you provide</li>
                  <li><strong className="text-foreground">Formula Limitations:</strong> All formulas are derived from population studies and may not apply to everyone equally</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Weight Loss Disclaimer</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Weight loss projections are estimates only. Actual results will vary based on many factors including diet, sleep, stress levels, genetics, medical conditions, and consistency of exercise. Rapid weight loss can be harmful; we recommend losing no more than 0.5-1 kg (1-2 lbs) per week unless supervised by a healthcare professional.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Exercise Disclaimer</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Before beginning any exercise program, including walking for weight loss, consult with your physician. This is especially important if you:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-4">
                  <li>Have been inactive for an extended period</li>
                  <li>Have heart disease, diabetes, or other chronic conditions</li>
                  <li>Are pregnant or nursing</li>
                  <li>Are over 40 years old and starting a new exercise program</li>
                  <li>Have joint problems or injuries</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">External Links Disclaimer</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our website may contain links to external websites for citation or additional information. We do not control the content of these sites and are not responsible for their accuracy or privacy practices. The inclusion of any link does not imply endorsement by Datalevo.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">No Liability</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Under no circumstances shall Datalevo or its team members be liable for any direct, indirect, incidental, consequential, special, or exemplary damages arising out of or in connection with your use of the Calculator or any information provided. You use this tool entirely at your own risk.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">User Responsibility</h2>
                <p className="text-muted-foreground leading-relaxed">
                  By using the Calculator, you acknowledge and agree that:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-4">
                  <li>You are responsible for verifying any information before acting on it</li>
                  <li>You will consult healthcare professionals for personalized advice</li>
                  <li>You understand the limitations of estimate-based calculations</li>
                  <li>You will not hold Datalevo liable for any outcomes</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Contact</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have questions about this Disclaimer, please contact us at:
                </p>
                <p className="text-muted-foreground mt-2">
                  <strong className="text-foreground">Email:</strong>{" "}
                  <a href="mailto:contact@datalevo.com" className="text-primary hover:underline">
                    contact@datalevo.com
                  </a>
                </p>
              </section>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Disclaimer;
